import Api from '../Api';

/** Как в `AddOrganization` / `DaysSection` */
export const organizationFormDays = [
  { name: 'Понедельник', value: 'monday' },
  { name: 'Вторник', value: 'tuesday' },
  { name: 'Среда', value: 'wednesday' },
  { name: 'Четверг', value: 'thursday' },
  { name: 'Пятница', value: 'friday' },
  { name: 'Суббота', value: 'saturday' },
  { name: 'Воскресенье', value: 'sunday' },
];

const DAY_KEYS = organizationFormDays.map((d) => d.value);

const defaultSchedule = organizationFormDays.reduce(
  (acc, day) => ({ ...acc, [day.value]: { start: '', end: '' } }),
  {},
);

export function stripJsonFromPastedText(raw) {
  if (typeof raw !== 'string') return '';
  const t = raw.trim();
  const fenced = t.match(/```(?:json)?\s*([\s\S]*?)```/i);
  if (fenced) return fenced[1].trim();
  return t;
}

/**
 * Загружает категории, страны, языки и все города по странам (как на форме выбора города).
 */
export async function fetchAllStaticDataForAutofill() {
  const [{ data: catData }, { data: countriesData }, { data: langData }] = await Promise.all([
    Api.getCategories(),
    Api.getCountries(),
    Api.getLanguages(),
  ]);

  const categories = catData?.categories || [];
  const countries = countriesData?.countries || [];
  const languages = langData?.languages || [];

  const citiesByCountryId = {};
  await Promise.all(
    countries.map(async (country) => {
      const { data } = await Api.getCities({ country_id: country._id });
      citiesByCountryId[country._id] = data?.cities || [];
    }),
  );

  return {
    categories,
    countries,
    languages,
    citiesByCountryId,
  };
}

/**
 * Справочник для файла и для «Преобразовать»: категории, языки, города только выбранной страны.
 * В citiesByCountryId — один ключ (countryId).
 */
export async function fetchAutofillDataForSelectedCountry(countryId) {
  if (!countryId) {
    throw new Error('countryId is required');
  }
  const [{ data: catData }, { data: countriesData }, { data: langData }, { data: citiesData }] = await Promise.all([
    Api.getCategories(),
    Api.getCountries(),
    Api.getLanguages(),
    Api.getCities({ country_id: countryId }),
  ]);

  const countries = countriesData?.countries || [];
  const country = countries.find((c) => c._id === countryId);
  if (!country) {
    throw new Error('Country not found');
  }

  const cities = citiesData?.cities || [];

  return {
    categories: catData?.categories || [],
    languages: langData?.languages || [],
    selectedCountry: country,
    citiesByCountryId: { [countryId]: cities },
  };
}

function findCategoryById(categories, categoryId) {
  if (!categoryId) return null;
  return categories.find((c) => c._id === categoryId) || null;
}

function resolveSubCategoriesByIds(category, subCategoryIds) {
  if (!category?.sub_categories?.length || !Array.isArray(subCategoryIds) || !subCategoryIds.length) {
    return [];
  }
  const subs = category.sub_categories;
  return subs.filter((s) => subCategoryIds.includes(s._id));
}

export function findCityById(citiesByCountryId, cityId) {
  if (!cityId) return null;
  let found = null;
  Object.keys(citiesByCountryId).forEach((cid) => {
    if (found) return;
    const list = citiesByCountryId[cid];
    const f = list.find((c) => c._id === cityId);
    if (f) found = f;
  });
  return found;
}

function resolveLanguagesByIds(languages, languageIds) {
  if (!Array.isArray(languageIds) || !languageIds.length) return [];
  return languages.filter((l) => languageIds.includes(l._id));
}

function normalizeDayValue(val) {
  if (val === undefined || val === null) return null;

  if (typeof val === 'string') {
    const s = val.trim();
    if (s.includes('-') && /^\d{1,2}:\d{2}-\d{1,2}:\d{2}$/.test(s.replace(/\s/g, ''))) {
      const clean = s.replace(/\s/g, '');
      const [start, end] = clean.split('-');
      return { type: 'hours', start, end };
    }
    return { type: s, start: '', end: '' };
  }

  if (typeof val === 'object' && val.type === 'hours') {
    return {
      type: 'hours',
      start: val.start != null ? String(val.start) : '',
      end: val.end != null ? String(val.end) : '',
    };
  }

  if (typeof val === 'object' && val.type) {
    return { type: val.type, start: '', end: '' };
  }

  return null;
}

/** Только дни, явно присутствующие в объекте schedule */
function normalizeScheduleInputPartial(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const out = {};
  DAY_KEYS.forEach((day) => {
    if (!Object.prototype.hasOwnProperty.call(raw, day)) return;
    const normalized = normalizeDayValue(raw[day]);
    if (normalized) out[day] = normalized;
  });
  return Object.keys(out).length ? out : null;
}

/** Только дни из work_time, как в API (частичное слияние с формой). */
export function scheduleFromWorkTimePartial(workTime) {
  if (!workTime || typeof workTime !== 'object') return null;
  const out = {};
  DAY_KEYS.forEach((day) => {
    if (!Object.prototype.hasOwnProperty.call(workTime, day)) return;
    const thisDayType = workTime[day];
    if (typeof thisDayType === 'string') {
      if (thisDayType.length === 11 && thisDayType.includes('-')) {
        const [start, end] = thisDayType.split('-');
        out[day] = { start, end, type: 'hours' };
      } else {
        out[day] = { start: '', end: '', type: thisDayType };
      }
    }
  });
  return Object.keys(out).length ? out : null;
}

const CONTACT_KEYS = ['phone_number', 'instagram', 'telegram', 'whatsapp', 'viber', 'facebook', 'website'];

/** Телефонные поля: в форме только цифры (без +, пробелов, скобок, дефисов). */
const PHONE_DIGIT_CONTACT_KEYS = ['phone_number', 'whatsapp', 'viber'];

export function normalizeContactDigits(value) {
  if (value == null || value === '') return '';
  return String(value).replace(/\D/g, '');
}

/**
 * Разбор JSON и сопоставление со справочниками.
 * Возвращает только поля, явно присутствующие в JSON (частичное заполнение).
 */
export function parseOrganizationAutofillJson(jsonString, staticData) {
  let data;
  try {
    data = JSON.parse(stripJsonFromPastedText(jsonString));
  } catch (e) {
    return { error: 'Не удалось разобрать JSON. Проверьте скобки и кавычки.' };
  }

  if (!data || typeof data !== 'object') {
    return { error: 'Корень JSON должен быть объектом.' };
  }

  const has = (k) => Object.prototype.hasOwnProperty.call(data, k);
  /** Ключ есть и значение не null — null значит «поле не трогать, не валидировать» */
  const take = (k) => has(k) && data[k] !== null;
  const categoryInJson = take('category_id');

  const {
    categories,
    languages,
    citiesByCountryId,
  } = staticData;

  const patches = {};

  if (take('owner_id')) patches.ownerId = String(data.owner_id).trim();
  if (take('name')) patches.name = String(data.name).trim();
  if (take('specialization')) patches.specialization = String(data.specialization).trim();
  if (take('address')) patches.address = String(data.address).trim();
  if (take('description')) patches.description = String(data.description).trim();

  if (take('schedule')) {
    const partial = normalizeScheduleInputPartial(data.schedule);
    if (partial) patches.schedulePartial = partial;
  } else if (take('work_time')) {
    const partial = scheduleFromWorkTimePartial(data.work_time);
    if (partial) patches.schedulePartial = partial;
  }

  if (take('contacts') && data.contacts && typeof data.contacts === 'object') {
    patches.contactsPartial = {};
    CONTACT_KEYS.forEach((k) => {
      if (Object.prototype.hasOwnProperty.call(data.contacts, k) && data.contacts[k] !== null) {
        const v = data.contacts[k];
        const raw = v != null ? String(v).trim() : '';
        patches.contactsPartial[k] = PHONE_DIGIT_CONTACT_KEYS.includes(k)
          ? normalizeContactDigits(raw)
          : raw;
      }
    });
  }

  let effectiveCategory = null;
  if (categoryInJson) {
    effectiveCategory = findCategoryById(categories, data.category_id);
    if (!effectiveCategory && data.category_id) {
      return { error: 'Категория не найдена по category_id.' };
    }
    patches.category = effectiveCategory;
  }

  if (take('sub_category_ids')) {
    if (!effectiveCategory) {
      return { error: 'Укажите category_id вместе с sub_category_ids.' };
    }
    const subIds = Array.isArray(data.sub_category_ids)
      ? data.sub_category_ids.filter((id) => id != null)
      : [];
    patches.subCategories = resolveSubCategoriesByIds(effectiveCategory, subIds);
  }

  if (categoryInJson && effectiveCategory?.is_online) {
    patches.city = null;
    patches.additionalCities = [];
    if (take('address')) patches.address = '';
    if (take('coordinates')) patches.coordinates = null;
  } else {
    if (take('city_id')) {
      const city = findCityById(citiesByCountryId, data.city_id);
      if (!city) {
        return { error: 'Город не найден по city_id.' };
      }
      patches.city = city;
    }

    if (take('additional_city_ids')) {
      const ids = Array.isArray(data.additional_city_ids) ? data.additional_city_ids : [];
      patches.additionalCities = ids
        .filter((id) => id != null)
        .map((id) => findCityById(citiesByCountryId, id))
        .filter(Boolean);
    }

    if (take('coordinates') && data.coordinates && (data.coordinates.lat != null || data.coordinates.lon != null)) {
      patches.coordinates = {
        lat: data.coordinates.lat != null ? String(data.coordinates.lat) : '',
        lon: data.coordinates.lon != null ? String(data.coordinates.lon) : '',
        is_special_point_on_map: Boolean(data.coordinates.is_special_point_on_map),
      };
    }
  }

  if (take('language_ids')) {
    const langIds = Array.isArray(data.language_ids)
      ? data.language_ids.filter((id) => id != null)
      : [];
    patches.languages = resolveLanguagesByIds(languages, langIds);
  }

  if (take('tags')) {
    patches.tags = Array.isArray(data.tags)
      ? data.tags.filter((t) => t != null).map((t) => String(t).trim()).filter(Boolean)
      : [];
  }

  if (take('template_photos_ids')) {
    patches.templatePhotoIds = Array.isArray(data.template_photos_ids)
      ? data.template_photos_ids.filter((id) => typeof id === 'string' && id.length === 24)
      : [];
  }

  return { patches };
}

export function buildAutofillReferenceFileContent(staticPayload) {
  const {
    categories,
    languages,
    selectedCountry,
    citiesByCountryId,
  } = staticPayload;

  if (!selectedCountry?._id) {
    throw new Error('selectedCountry is required');
  }

  const countryId = selectedCountry._id;
  const cities = citiesByCountryId[countryId] || [];

  const referenceData = {
    selected_country: { _id: selectedCountry._id, name: selectedCountry.name },
    cities: cities.map((city) => ({
      _id: city._id,
      name: city.name,
      country_id: city.country_id || countryId,
    })),
    categories: categories.map((c) => ({
      _id: c._id,
      name: c.name,
      is_online: Boolean(c.is_online),
      sub_categories: (c.sub_categories || []).map((s) => ({ _id: s._id, name: s.name })),
    })),
    languages: languages.map((l) => ({ _id: l._id, name: l.name })),
  };

  const schemaDescription = {
    description: 'Один JSON-объект для формы «Добавить организацию». Файл сформирован для одной страны: см. selected_country. В cities перечислены только города этой страны. Поля опускай, если в тексте нет данных. Справочники (категории, города, языки) сопоставляй по смыслу с REFERENCE_DATA, но в JSON пиши ТОЛЬКО _id — никогда не подставляй вместо id человекочитаемые названия.',
    fields: {
      owner_id: '24-символьный hex ID владельца в системе',
      name: 'Название организации',
      category_id: 'строка — ровно categories[i]._id',
      sub_category_ids: ['массив строк — только sub_categories[j]._id выбранной категории'],
      specialization: 'строка',
      city_id: 'строка — ровно cities[i]._id из REFERENCE_DATA.cities (только эта страна, см. selected_country)',
      additional_city_ids: ['массив строк — только _id из REFERENCE_DATA.cities; все города — той же selected_country'],
      address: 'строка',
      coordinates: { lat: 'число или строка', lon: 'число или строка', is_special_point_on_map: false },
      schedule: 'объект дней monday..sunday; каждый день: { type: "hours", start, end } или { type: "day_off"|"temp_closed"|"whole_day" } или строка "09:00-18:00" / "day_off"',
      work_time: 'альтернатива schedule в формате API: monday: "09:00-18:00" или "day_off"',
      contacts: 'Объект: phone_number, whatsapp, viber — только цифры подряд (если в тексте +7 (999) 123-45-67, в JSON пиши 79991234567). instagram, telegram, facebook, website — как строки без изменения формата.',
      description: 'строка',
      language_ids: 'ОБЯЗАТЕЛЬНО: массив строк, каждая строка = languages[n]._id (поле _id из REFERENCE_DATA.languages). ЗАПРЕЩЕНО класть в language_ids названия языков (например «Русский») — только id из справочника.',
      tags: 'массив коротких строк — ключевые слова/темы по тексту; не из справочника. Если из текста можно вывести хотя бы одну тему — заполни минимум 1–3 тега, не оставляй пустым без причины.',
      template_photos_ids: ['опционально _id шаблонных фото'],
    },
    json_syntax: 'Ответ одним сообщением: ТОЛЬКО markdown-блок с языком json, внутри — один валидный объект как после JSON.stringify (допустимы отступы как JSON.stringify(obj, null, 2)). Никакого текста до ```json и после закрывающего ```. В строковых полях (address, description, name и т.д.) внутренние кавычки " только как \\"; без сырых переводов строк внутри значений там, где ломается синтаксис JSON. Поля содержания — без markdown/HTML внутри JSON; админка извлекает текст из блока и передаёт в JSON.parse.',
    note: 'Для онлайн-категории (is_online: true) не включай city_id / additional_city_ids, если организация онлайн. Телефоны в contacts.phone_number, whatsapp, viber — строка из одних цифр, без плюса, скобок и разделителей.',
  };

  const prompt = [
    'Ты — помощник по заполнению карточки организации в админке Nomad.',
    '',
    'Порядок работы:',
    '— Сначала человек пришлёт тебе этот файл (справочник и инструкцию) одним сообщением. На это сообщение ты НЕ генерируешь JSON.',
    '— Если задача понятна, ответь ровно этой фразой и больше ничего: «Задача ясна, отправь текст для преобразования». Дальше жди следующих сообщений.',
    '— Когда в следующих сообщениях пришлют обычный текст об организации (описание, контакты, режим и т.д.), выполни шаги ниже и верни результат.',
    '',
    'Ниже в REFERENCE_DATA — selected_country и cities (только города ЭТОЙ одной выбранной при создании файла страны; других стран в списке нет), категории с подкатегориями, языки (у записей есть _id и name; name только для сопоставления с текстом).',
    '',
    'КРИТИЧНО — города (city_id, additional_city_ids):',
    '— city_id / additional_city_ids бери ТОЛЬКО из REFERENCE_DATA.cities; каждый _id должен буквально присутствовать в cities.',
    '— Не подставляй _id города, которого нет в cities (другой страны или устаревший список).',
    '',
    'КРИТИЧНО — языки (language_ids):',
    '— В JSON поле language_ids — это ВСЕГДА массив строк-идентификаторов: скопируй значение поля _id из нужных объектов в REFERENCE_DATA.languages.',
    '— ЗАПРЕЩЕНО писать в language_ids человекочитаемые названия («Русский», «English», «Հայերեն» и т.п.). Так программа не сможет разобрать ответ.',
    '— Алгоритм: по тексту понял язык → нашёл объект в languages с подходящим name → в массив language_ids добавил ТОЛЬКО его строку _id.',
    '— Пример правильно: "language_ids": ["507f1f77bcf86cd799439011", "507f191e810c19729de860ea"]',
    '— Пример НЕПРАВИЛЬНО: "language_ids": ["Русский", "English"]',
    '',
    'КРИТИЧНО — теги (tags):',
    '— Это массив обычных строк-слов/фраз из смысла текста (не из справочника id).',
    '— Выведи минимум 1–3 тега, если в тексте есть хоть какая-то предметная информация (услуги, тема, тип бизнеса). Пустой tags допустим только если текста совсем нет.',
    '',
    'КРИТИЧНО — внутренние двойные кавычки в тексте (address, description, name, specialization, contacts и любые строки):',
    '— В JSON одна пара "…" задаёт границы строки; любая «лишняя» " внутри текста обязана быть экранирована как \\" (как делает JSON.stringify). Без этого JSON невалиден.',
    '— Пример: адрес с названием в кавычках — пиши ... \\"El Camisón\\" ... внутри значения, а не голые кавычки вокруг слова.',
    '',
    'КРИТИЧНО — телефоны (contacts.phone_number, contacts.whatsapp, contacts.viber):',
    '— В JSON значение — только цифры 0–9 подряд, без +, пробелов, скобок, дефисов.',
    '— Если в исходном тексте номер в виде +7 (999) 123-45-67 или 8-999-123-45-67 — оставь в JSON только цифры в том порядке, как в номере (например 79991234567 или 89991234567 по смыслу страны).',
    '— Поля instagram, telegram, facebook, website не являются телефонами — не режь их до цифр.',
    '',
    'КРИТИЧНО — формат ответа:',
    '— Всегда возвращай результат ТОЛЬКО внутри блока кода с типом json:',
    '```json',
    '{...}',
    '```',
    '— Без текста до открывающего ```json и после закрывающих ```.',
    '— Внутри фигурных скобок — один валидный JSON (содержимое как после JSON.stringify): никаких **жирного** / _курсива_ / # вне строковых значений, никакого HTML как разметки вокруг данных. Строковые значения полей — обычный текст или экранирование; кликабельные markdown-ссылки не использовать.',
    '— Запрещено в содержимом JSON: одинарные кавычки у ключей, запятая после последнего элемента, комментарии // или /* */, ключи без кавычек, значения undefined, неэкранированные двойные кавычки внутри строкового значения.',
    '— Внутри строк JSON символ " только как \\" — как делает JSON.stringify.',
    '— Проверка: из твоего ответа извлекают текст между ```json и ``` → он без ошибки проходит через JSON.parse.',
    '',
    'Когда пришёл текст для преобразования, твоя задача:',
    '1) Извлеки из него всё, что относится к полям из schemaDescription.fields.',
    '2) Для категории, подкатегорий, города, доп. городов, языков подставляй только _id из REFERENCE_DATA (города — только из cities и только для selected_country; см. блоки выше).',
    '3) Не выдумывай id: каждый id должен буквально совпадать с каким-либо _id из соответствующего списка в REFERENCE_DATA.',
    '4) График (schedule): для каждого дня monday..sunday — часы { "type": "hours", "start", "end" } или тип day_off / temp_closed / whole_day.',
    '5) Верни ответ только в виде одного блока ```json … ``` по правилам «КРИТИЧНО — формат ответа» выше.',
    '',
    'SCHEMA_HINT:',
    JSON.stringify(schemaDescription, null, 2),
    '',
    'REFERENCE_DATA:',
    JSON.stringify(referenceData, null, 2),
  ].join('\n');

  return [
    '══════════════════════════════════════════════════════════════',
    'NOMAD — автозаполнение организации (справочник + инструкция для ИИ)',
    '══════════════════════════════════════════════════════════════',
    '',
    'Как пользоваться (оператору):',
    '1) В админке при скачивании файла выбрана одна страна: в REFERENCE_DATA в поле selected_country и в списке cities — только эта страна. Скачивай и используй в чате с моделью один файл для выбранной страны.',
    '2) Сохрани этот файл при необходимости.',
    '3) Первым сообщением отправь в чат с языковой моделью весь этот файл (или целиком блок ниже: SCHEMA_HINT, затем REFERENCE_DATA и инструкция для модели).',
    '4) Дождись ответа модели. Если задача ясна, она должна написать: «Задача ясна, отправь текст для преобразования».',
    '5) Следующим сообщением (или несколькими) отправь обычный текст об организации.',
    '6) Скопируй ответ модели целиком (включая блок ```json … ``` или только голый JSON — админка вытаскивает содержимое блока). В поле «Автозаполнение» выбери ту же страну, что при скачивании, нажми «Преобразовать». Иначе _id городов не совпадут со справочником.',
    '7) Если ответ ИИ не принимается системой (неверная структура или синтаксис), открой https://jsonviewer.stack.hu/ — вставь текст, нажми «Format», исправь по подсказкам при необходимости и скопируй JSON обратно.',
    '',
    prompt,
  ].join('\n');
}
